import type { ElasticSearchResponseDocumentListPaginationPage } from '@//ElasticSearch';

export interface PaginationItem {
  displayName: string;
  selected?: boolean;
  query?: string;
  isEllipsis?: boolean;
}

export const limitNumberOfPaginationPages = (
  pages: ElasticSearchResponseDocumentListPaginationPage[],
  limit: number = 5,
): ElasticSearchResponseDocumentListPaginationPage[] => {
  // If there are 5 or fewer pages, return the entire array.
  if (pages.length <= limit) {
    return pages;
  }

  // Find the index of the currently selected page.
  const currentIndex = pages.findIndex((page) => page.selected);

  // Calculate the "half window" size for a maximum of 5 pages (i.e. 2 pages on either side).
  const half = Math.floor(limit / 2);

  // Determine tentative start and end indexes.
  let startIndex = currentIndex - half;
  let endIndex = currentIndex + half + 1; // +1 because slice's end is exclusive.

  // Adjust if the selected page is too near the beginning.
  if (startIndex < 0) {
    startIndex = 0;
    endIndex = limit;
  }

  // Adjust if the selected page is too near the end.
  if (endIndex > pages.length) {
    endIndex = pages.length;
    startIndex = pages.length - limit;
  }

  // Return the sliced pagination items.
  return pages.slice(startIndex, endIndex);
};

export const createPaginationWithEllipsis = (
  pages: ElasticSearchResponseDocumentListPaginationPage[],
  maxVisible: number = 5,
): PaginationItem[] => {
  if (pages.length <= maxVisible) {
    return pages.map((page) => ({
      displayName: page.displayName,
      selected: page.selected,
      query: page.query,
    }));
  }

  const currentIndex = pages.findIndex((page) => page.selected);
  const firstPage = pages[0];
  const lastPage = pages[pages.length - 1];

  // Always show first and last page
  const result: PaginationItem[] = [];

  // Calculate how many pages we can show in the middle (excluding first/last and ellipsis)
  const middleSlots = maxVisible - 2; // Reserve slots for first and last page
  const sidePages = Math.floor((middleSlots - 1) / 2); // Pages on each side of current page

  // Add first page
  result.push({
    displayName: firstPage.displayName,
    selected: firstPage.selected,
    query: firstPage.query,
  });

  let start = Math.max(1, currentIndex - sidePages);
  let end = Math.min(pages.length - 1, currentIndex + sidePages + 1);

  // Adjust if we're near the beginning
  if (currentIndex - sidePages <= 1) {
    end = Math.min(pages.length - 1, middleSlots);
  }

  // Adjust if we're near the end
  if (currentIndex + sidePages >= pages.length - 2) {
    start = Math.max(1, pages.length - middleSlots - 1);
  }

  // Add ellipsis before middle section if needed
  if (start > 1) {
    result.push({
      displayName: '...',
      isEllipsis: true,
    });
  }

  // Add middle pages
  for (let i = start; i < end; i++) {
    result.push({
      displayName: pages[i].displayName,
      selected: pages[i].selected,
      query: pages[i].query,
    });
  }

  // Add ellipsis after middle section if needed
  if (end < pages.length - 1) {
    result.push({
      displayName: '...',
      isEllipsis: true,
    });
  }

  // Add last page (if not already included)
  // Check if the last page hasn't been added to the result yet
  const lastPageIncluded = result.some(
    (item) => !item.isEllipsis && item.displayName === lastPage.displayName,
  );
  if (!lastPageIncluded) {
    result.push({
      displayName: lastPage.displayName,
      selected: lastPage.selected,
      query: lastPage.query,
    });
  }

  return result;
};
